/**
 * Image Optimization Utilities
 * 
 * Handles:
 * - Supabase Storage URL generation with transformations
 * - CDN-backed delivery
 * - Signed URLs for private images
 * - Responsive image sizing
 */

import { supabase } from "@/integrations/supabase/client";

interface ImageTransformOptions {
  width?: number;
  height?: number;
  quality?: number;
  format?: "webp" | "avif" | "jpeg" | "png";
  resize?: "cover" | "contain" | "fill";
}

/**
 * Generate optimized Supabase Storage URL with transformations
 * 
 * @param bucket - Storage bucket name
 * @param path - File path in bucket
 * @param options - Transformation options
 * @returns Optimized image URL
 */
export const getOptimizedImageUrl = (
  bucket: string,
  path: string,
  options: ImageTransformOptions = {}
): string => {
  const {
    width,
    height,
    quality = 75,
    format,
    resize = "contain",
  } = options;

  // Build transformation parameters
  const params = new URLSearchParams();
  
  if (width) params.append("width", width.toString());
  if (height) params.append("height", height.toString());
  if (quality !== 75) params.append("quality", quality.toString());
  if (format) params.append("format", format);
  if (resize !== "contain") params.append("resize", resize);

  const baseUrl = supabase.storage.from(bucket).getPublicUrl(path).data.publicUrl;
  const queryString = params.toString();
  
  return queryString ? `${baseUrl}?${queryString}` : baseUrl;
};

/**
 * Generate signed URL for private images
 * 
 * @param bucket - Storage bucket name
 * @param path - File path in bucket
 * @param expiresIn - URL expiration in seconds (default: 1 hour)
 * @returns Signed URL or null on error
 */
export const getSignedImageUrl = async (
  bucket: string,
  path: string,
  expiresIn: number = 3600
): Promise<string | null> => {
  const { data, error } = await supabase.storage
    .from(bucket)
    .createSignedUrl(path, expiresIn);

  if (error) {
    console.error("Failed to generate signed URL:", error);
    return null;
  }

  return data.signedUrl;
};

/**
 * Generate responsive srcSet string for an image
 * 
 * @param bucket - Storage bucket name
 * @param path - File path in bucket
 * @param widths - Array of widths to generate
 * @returns srcSet string
 */
export const generateResponsiveSrcSet = (
  bucket: string,
  path: string,
  widths: number[] = [640, 750, 828, 1080, 1200, 1920]
): string => {
  return widths
    .map((width) => {
      const url = getOptimizedImageUrl(bucket, path, {
        width,
        quality: 75,
        format: "webp",
      });
      return `${url} ${width}w`;
    })
    .join(", ");
};

/**
 * Optimize uploaded image before storage
 * 
 * @param file - File to optimize
 * @param maxWidth - Maximum width
 * @param maxHeight - Maximum height
 * @param quality - Compression quality (0-1)
 * @returns Optimized blob
 */
export const optimizeImageUpload = async (
  file: File,
  maxWidth: number = 1920,
  maxHeight: number = 1920,
  quality: number = 0.8
): Promise<Blob> => {
  return new Promise((resolve, reject) => {
    const img = new Image();
    const canvas = document.createElement("canvas");
    const ctx = canvas.getContext("2d");

    if (!ctx) {
      reject(new Error("Failed to get canvas context"));
      return;
    }

    img.onload = () => {
      // Calculate new dimensions
      let { width, height } = img;
      
      if (width > maxWidth) {
        height = (height * maxWidth) / width;
        width = maxWidth;
      }
      
      if (height > maxHeight) {
        width = (width * maxHeight) / height;
        height = maxHeight;
      }

      // Resize on canvas
      canvas.width = width;
      canvas.height = height;
      ctx.drawImage(img, 0, 0, width, height);

      // Convert to blob
      canvas.toBlob(
        (blob) => {
          if (blob) {
            resolve(blob);
          } else {
            reject(new Error("Failed to convert canvas to blob"));
          }
        },
        "image/webp",
        quality
      );
    };

    img.onerror = () => {
      reject(new Error("Failed to load image"));
    };

    img.src = URL.createObjectURL(file);
  });
};

/**
 * Get avatar URL with optimization
 * 
 * @param userId - User ID
 * @param size - Desired size (px)
 * @returns Optimized avatar URL
 */
export const getAvatarUrl = (
  userId: string,
  size: number = 40
): string => {
  return getOptimizedImageUrl(
    "avatars",
    `${userId}/avatar.webp`,
    {
      width: size,
      height: size,
      quality: 85,
      format: "webp",
      resize: "cover",
    }
  );
};

/**
 * Get document thumbnail URL
 * 
 * @param documentId - Document ID
 * @param page - Page number (default: 1)
 * @returns Thumbnail URL
 */
export const getDocumentThumbnail = (
  documentId: string,
  page: number = 1
): string => {
  return getOptimizedImageUrl(
    "document-thumbnails",
    `${documentId}/page-${page}.webp`,
    {
      width: 200,
      height: 280,
      quality: 75,
      format: "webp",
      resize: "cover",
    }
  );
};

/**
 * Get empty state illustration URL
 * 
 * @param name - Illustration name (e.g., "chat-empty", "documents-empty")
 * @returns Optimized illustration URL
 */
export const getEmptyStateIllustration = (name: string): string => {
  return getOptimizedImageUrl(
    "empty-states",
    `${name}.webp`,
    {
      width: 200,
      height: 200,
      quality: 85,
      format: "webp",
    }
  );
};

/**
 * Preload critical images
 * 
 * @param urls - Array of image URLs to preload
 */
export const preloadImages = (urls: string[]): void => {
  urls.forEach((url) => {
    const link = document.createElement("link");
    link.rel = "preload";
    link.as = "image";
    link.href = url;
    document.head.appendChild(link);
  });
};

/**
 * Convert base64 to blob for upload
 * 
 * @param base64 - Base64 encoded image
 * @param mimeType - MIME type
 * @returns Blob
 */
export const base64ToBlob = (
  base64: string,
  mimeType: string = "image/png"
): Blob => {
  const byteString = atob(base64.split(",")[1]);
  const ab = new ArrayBuffer(byteString.length);
  const ia = new Uint8Array(ab);
  
  for (let i = 0; i < byteString.length; i++) {
    ia[i] = byteString.charCodeAt(i);
  }
  
  return new Blob([ab], { type: mimeType });
};
